import type { CategoriaDeAuditoria, EntradaDeAuditoria } from '../../api/ops/backoffice';
import type { NombreIcono } from '../../componentes/iconos';

/**
 * Lógica sin estilos de la **Auditoría de plataforma** (1.21, prototipo `AUD` y `AUD_F`): las pestañas por categoría
 * (G183), la frase de cada acción del equipo de Reservaria, los días y la hora. Como en «Actividad» (1.19), una acción
 * que no está en `FRASES` se enseña con su propio código: nunca se inventa una frase.
 */
export const PESTANAS: readonly { clave: CategoriaDeAuditoria | null; etiqueta: string; icono: NombreIcono }[] = [
    { clave: null, etiqueta: 'Todo', icono: 'list' },
    { clave: 'accesses', etiqueta: 'Accesos', icono: 'headset' },
    { clave: 'businesses', etiqueta: 'Negocios', icono: 'building-2' },
    { clave: 'prices', etiqueta: 'Precios', icono: 'tags' },
    { clave: 'partners', etiqueta: 'Partners', icono: 'handshake' },
    { clave: 'ycloud', etiqueta: 'YCloud', icono: 'scale' },
];

/** La búsqueda va a la API desde 2 letras (`q`, de 2 a 100). */
export const MINIMO_BUSQUEDA = 2;

export function textoBusqueda(texto: string): string | undefined {
    const q = texto.trim().slice(0, 100);
    return q.length >= MINIMO_BUSQUEDA ? q : undefined;
}

/** [verbo, objeto cuando no hay negocio, icono]. Por código de acción (`new Accion(...)` de los módulos). */
export const FRASES: Readonly<Record<string, readonly [string, string, NombreIcono]>> = {
    'plataforma.acceso.abrir': ['entró en solo lectura en', 'un negocio', 'eye'],
    'plataforma.equipos.ver': ['vio el equipo de', 'un negocio', 'users'],
    'planes.prueba.ampliar': ['amplió la prueba de', 'un negocio', 'calendar-plus'],
    'planes.cupon.aplicar': ['aplicó un cupón a', 'un negocio', 'percent'],
    'planes.cupon.crear': ['creó', 'un cupón', 'percent'],
    'plataforma.partners.aprobar': ['aprobó', 'a un partner', 'handshake'],
    'plataforma.partners.rechazar': ['rechazó', 'a un partner', 'handshake'],
    'plataforma.partners.atribuir': ['atribuyó a un partner el negocio', 'un negocio', 'handshake'],
    'plataforma.partners.quitar': ['quitó el partner de', 'un negocio', 'handshake'],
    'plataforma.partners.datos_legales': ['cambió los datos legales de', 'un partner', 'handshake'],
    'plataforma.partners.liquidacion.pagar': ['marcó pagada', 'una liquidación de partner', 'wallet'],
    'plataforma.pagos.avisos': ['actuó sobre un aviso de Stripe de', 'un negocio', 'refresh-cw'],
    'plataforma.canales.avisos.reprocesar': ['reintentó un aviso de', 'mensajería', 'refresh-cw'],
    'plataforma.canales.avisos.cerrar': ['marcó como resuelto un aviso de', 'mensajería', 'check'],
    'plataforma.indicadores.reconstruir': ['reconstruyó', 'un indicador', 'refresh-cw'],
    'plataforma.indicadores.aprobar': ['aprobó', 'una versión de un indicador', 'check'],
    'plataforma.indicadores.descartar': ['descartó', 'una versión de un indicador', 'x'],
};

export interface FraseAuditoria {
    actor: string;
    verbo: string;
    objeto: string;
    motivo: string;
    icono: NombreIcono;
    /** Suplantación: la baldosa en rojo (prototipo `user-cog`). */
    roja: boolean;
}

/** Quién lo hizo: la persona real del equipo (`real_actor`) o, si no viene, el nombre que da la API (G334). */
export function actorDe(e: EntradaDeAuditoria): string {
    const real = e.real_actor?.display_name?.trim() ?? '';
    return real !== '' ? real : e.actor.display_name;
}

export function fraseDe(e: EntradaDeAuditoria, negocio: string | undefined): FraseAuditoria {
    const frase = FRASES[e.action];
    const suplanto = e.action === 'plataforma.acceso.abrir' && (e.changes as { mode?: unknown } | null | undefined)?.mode === 'impersonation';
    if (frase === undefined) {
        return { actor: actorDe(e), verbo: e.action, objeto: negocio ?? '', motivo: e.reason ?? '', icono: 'history', roja: false };
    }
    return {
        actor: actorDe(e),
        verbo: suplanto ? 'suplantó al propietario de' : frase[0],
        objeto: negocio ?? frase[1],
        motivo: e.reason ?? '',
        icono: suplanto ? 'user-cog' : frase[2],
        roja: suplanto,
    };
}

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const inicioDelDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** «Hoy», «Ayer» o «12 oct» (prototipo `AUD`). */
export function etiquetaDia(fecha: Date, ahora: Date): string {
    const dias = Math.round((inicioDelDia(ahora) - inicioDelDia(fecha)) / 86_400_000);
    if (dias === 0) {
        return 'Hoy';
    }
    if (dias === 1) {
        return 'Ayer';
    }
    return `${String(fecha.getDate())} ${MESES_CORTOS[fecha.getMonth()] ?? ''}`;
}

export function horaDe(fecha: Date): string {
    return `${String(fecha.getHours()).padStart(2, '0')}:${String(fecha.getMinutes()).padStart(2, '0')}`;
}

/** «Soporte» o «Superadmin»: el rol de plataforma que tenía al actuar (`actor.platform_role`, G1141); si no, null. */
export function rolDe(e: EntradaDeAuditoria): string | null {
    const rol = e.actor.platform_role ?? null;
    return rol === 'superadmin' ? 'Superadmin' : rol === 'support' ? 'Soporte' : null;
}

/** La línea gris de la fila (prototipo `{{ r.time }} · {{ r.role }}`): «09:31 · Superadmin». */
export function lineaHora(e: EntradaDeAuditoria): string {
    const rol = rolDe(e);
    const hora = horaDe(new Date(e.occurred_at));
    return rol === null ? hora : `${hora} · ${rol}`;
}

/** Resultado de `verifyAuditChain` (G1144, C.65). */
export interface ResultadoCadena {
    intact: boolean;
    entries_checked: number;
    first_broken_seq?: number | null;
}

const conPuntos = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/**
 * El resultado de «Comprobar la cadena» (C.65, G1144), título y explicación, como el dibujo 1.22: «La cadena está intacta» ·
 * «1.284 entradas comprobadas: nadie ha cambiado ni borrado nada.» o «La cadena se rompe en la entrada 1.203» · «De la
 * entrada 1.203 en adelante no se puede asegurar que nada se haya cambiado o borrado. Las anteriores, sí.»
 */
export function textoCadena(r: ResultadoCadena): { titulo: string; texto: string } {
    if (r.intact) {
        return { titulo: 'La cadena está intacta', texto: `${r.entries_checked === 1 ? '1 entrada comprobada' : `${conPuntos(r.entries_checked)} entradas comprobadas`}: nadie ha cambiado ni borrado nada.` };
    }
    const n = r.first_broken_seq;
    // Sin número de entrada (el contrato lo permite nulo), sin decir dónde: hueco anotado como pregunta.
    return n === null || n === undefined
        ? { titulo: 'La cadena está rota', texto: 'No se puede asegurar que nada se haya cambiado o borrado.' }
        : { titulo: `La cadena se rompe en la entrada ${conPuntos(n)}`, texto: `De la entrada ${conPuntos(n)} en adelante no se puede asegurar que nada se haya cambiado o borrado. Las anteriores, sí.` };
}

export const acciones = (n: number): string => (n === 1 ? '1 acción' : `${String(n)} acciones`);

/**
 * «N acciones del equipo de Reservaria en este negocio» con `total_count` (G1290); sin él, nada. La cifra cuenta con la
 * pestaña y la búsqueda aplicadas (G1326, C.86): con alguna, «…en este negocio, con este filtro».
 */
export const accionesDelNegocio = (n: number | null | undefined, filtrado = false): string | null =>
    n == null ? null : `${n === 1 ? '1 acción' : `${conPuntos(n)} acciones`} del equipo de Reservaria en este negocio${filtrado ? ', con este filtro' : ''}`;

/** Entradas por día, en el orden en que llegan (de la más reciente a la más antigua). */
export function porDias(entradas: readonly EntradaDeAuditoria[], ahora: Date): { etiqueta: string; entradas: EntradaDeAuditoria[] }[] {
    const grupos: { etiqueta: string; entradas: EntradaDeAuditoria[] }[] = [];
    for (const e of entradas) {
        const etiqueta = etiquetaDia(new Date(e.occurred_at), ahora);
        const ultimo = grupos[grupos.length - 1];
        if (ultimo?.etiqueta === etiqueta) {
            ultimo.entradas.push(e);
        } else {
            grupos.push({ etiqueta, entradas: [e] });
        }
    }
    return grupos;
}

export const sinResultados = (q: string): string => `Nada en la auditoría con «${q}». Prueba con otra palabra o cambia de pestaña.`;
